import { useState, useEffect, useMemo } from "react";
import { useController } from "react-hook-form";
import { Plus } from "lucide-react";
import type { Lens } from "@hookform/lenses";
import type { ActorRefFrom } from "xstate";
import { cn } from "@design-lab/shared/lib";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import type { transferArtworkMachine } from "@design-lab/entities/transfers-lab/model/transfer-artwork.machine";
import { TransferTypeForm } from "./TransferTypeForm";
import type { ArtworkFormData } from "../../model/transferLabTypes";
import { getArtworkAspectRatio } from "../../utils/artworkHelpers";
import { calculateTransferBounds } from "../../utils/calculateBounds";

export type TransfersFormProps = {
  artworkId: string;
  artworkLens: Lens<ArtworkFormData>;
  onAddTransferType?: () => void;
  enhancementTier?: string; // Enhancement tier for pricing
  artworkActor?: ActorRefFrom<typeof transferArtworkMachine>;
};

export function TransfersForm(props: TransfersFormProps) {
  const { artworkId, artworkLens, onAddTransferType, enhancementTier, artworkActor } = props;

  // Get artwork data using controller with lens
  const { field } = useController(artworkLens.interop());
  const artwork = field.value;

  const transferTypes = useMemo(() => artwork?.transferTypes || {}, [artwork?.transferTypes]);

  // Get ordered transfer type IDs
  const transferTypeOrder = useMemo(() => Object.keys(transferTypes), [transferTypes]);

  // Get artwork metadata from actor for aspect ratio calculation
  const artworkData = artworkActor?.getSnapshot()?.context?.artwork;
  const aspectRatio = useMemo(() => getArtworkAspectRatio(artworkData), [artworkData]);
  const bounds = useMemo(() => calculateTransferBounds(artworkData), [artworkData]);

  // State for active tab
  const [selectedTransferTypeId, setSelectedTransferTypeId] = useState(transferTypeOrder[0] || "");

  // Create a stable string representation of the transfer type order for dependency tracking
  const transferTypeOrderKey = transferTypeOrder.join(",");

  // Sync selected transfer type when artwork or transfer types change
  useEffect(() => {
    // Check if current selected type exists in the new artwork
    if (selectedTransferTypeId && !transferTypeOrder.includes(selectedTransferTypeId)) {
      // If not, switch to the first available transfer type
      const firstAvailable = transferTypeOrder[0];
      if (firstAvailable) {
        setSelectedTransferTypeId(firstAvailable);
      }
    }
    // If there are transfer types but none selected, select the first one
    else if (!selectedTransferTypeId && transferTypeOrder.length > 0) {
      const firstType = transferTypeOrder[0];
      if (firstType) {
        setSelectedTransferTypeId(firstType);
      }
    }
  }, [artworkId, transferTypeOrderKey, selectedTransferTypeId, transferTypeOrder]); // Dependencies: artwork ID, transfer type order, and selected type

  const handleTabClick = (typeId: string) => {
    setSelectedTransferTypeId(typeId);
  };

  // Show empty state if no transfer types
  if (transferTypeOrder.length === 0) {
    return (
      <section className="mt-4 space-y-4">
        <header className="flex items-center justify-between">
          <span className="text-base font-semibold text-gray-900">Transfer Type (0)</span>
          {onAddTransferType && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onAddTransferType}
              data-testid="open-transfer-type-modal"
            >
              <Plus className="mr-1 h-4 w-4" />
              Add Transfer Type
            </Button>
          )}
        </header>
        <p className="text-sm text-gray-500">No transfer types selected. Add one to continue.</p>
      </section>
    );
  }

  const activeTransferType = selectedTransferTypeId ? transferTypes[selectedTransferTypeId] : null;

  return (
    <section className="mt-4 space-y-4">
      {/* Header with count and Add button */}
      <header className="flex items-center justify-between">
        <span className="text-base font-semibold text-gray-900">
          Transfer Type ({transferTypeOrder.length})
        </span>
        {onAddTransferType && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAddTransferType}
            data-testid="open-transfer-type-modal"
          >
            <Plus className="mr-1 h-4 w-4" />
            Add Transfer Type
          </Button>
        )}
      </header>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6" aria-label="Transfer types">
          {transferTypeOrder.map((typeId: string) => {
            const type = transferTypes[typeId];
            if (!type) return null;

            return (
              <button
                key={`${artworkId}-${typeId}`}
                type="button"
                onClick={() => handleTabClick(typeId)}
                className={cn(
                  "whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors",
                  selectedTransferTypeId && selectedTransferTypeId === typeId
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                )}
                aria-current={
                  selectedTransferTypeId && selectedTransferTypeId === typeId ? "page" : undefined
                }
                data-testid={`transfer-type-tab-${typeId}`}
              >
                {type.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content - single TransferTypeForm with active transfer type */}
      {activeTransferType && selectedTransferTypeId && (
        <div
          className="space-y-6"
          role="tabpanel"
          aria-labelledby={`tab-${selectedTransferTypeId}`}
        >
          <TransferTypeForm
            key={`${artworkId}-${selectedTransferTypeId}-${transferTypeOrderKey}`} // Unique key includes artwork ID, selected type, and all available types
            transferTypeId={selectedTransferTypeId}
            transferTypeLens={artworkLens.focus(`transferTypes.${selectedTransferTypeId}`)}
            artworkId={artworkId}
            aspectRatio={aspectRatio}
            bounds={bounds}
            {...(enhancementTier && { enhancementTier })}
          />
        </div>
      )}
    </section>
  );
}
